import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ApiService} from '../services/api.service';
import {ReportingDatePipe} from '../shared/datetime/reporting-date.pipe';

type HealthComponentState = {
    status?: string;
    detail?: string;
    backend?: string;
    jobs?: unknown[];
};

type StackCard = {
    key: string;
    title: string;
};

/** Default required stack order when backend omits active_components (older payloads). */
const DEFAULT_ACTIVE_COMPONENTS = ['api', 'database', 'storage'];

/**
 * Titles for backend platform-health keys.
 * celery_broker is the Docker RabbitMQ dependency — one card, not a separate RabbitMQ row.
 */
const COMPONENT_TITLES: Record<string, string> = {
    api: 'API',
    database: 'Database',
    storage: 'Storage',
    redis: 'Redis',
    rabbitmq: 'RabbitMQ',
    celery_broker: 'RabbitMQ / Celery broker',
    celery_worker: 'Celery worker',
    scheduler: 'Scheduler',
};

/** Browser-local: successful render of this page proves frontend availability. */
const FRONTEND_LOCAL_STATUS: HealthComponentState = {
    status: 'ok',
    detail: 'Health Status page loaded',
};

/**
 * Fallback when an older backend omits scheduler from the payload.
 * Prefer backend durable heartbeat status when present.
 */
const SCHEDULER_FALLBACK_STATUS: HealthComponentState = {
    status: 'unknown',
    detail: 'Scheduler status unavailable from platform health payload.',
};

/**
 * Staff-only Health Status page (route: /health, AuthGuard + SuperUserGuard).
 * Renders GET /v1/platform-health/ (Kyra Super User) plus a local Frontend card.
 * Version / environment / timestamp appear only in the overall banner (not as cards).
 * No public Django HTTP health path — Compose uses native Redis/RabbitMQ probes.
 */
@Component({
    selector: 'app-service-health-status',
    imports: [CommonModule, ReportingDatePipe],
    templateUrl: './service-health-status.component.html',
    styleUrl: './service-health-status.component.css'
})
export class ServiceHealthStatusComponent implements OnInit {
    platformHealth: any = null;
    loading = false;
    errorMessage = '';

    constructor(private apiService: ApiService) {
    }

    ngOnInit() {
        this.refresh();
    }

    refresh(): void {
        this.loading = true;
        this.errorMessage = '';
        this.apiService.platformHealth().subscribe({
            next: (data: any) => {
                this.platformHealth = data;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.platformHealth = null;
                this.errorMessage = 'Unable to load platform health status.';
            },
        });
    }

    /**
     * Cards for the Health Status grid:
     * Frontend (local) → backend active stack (includes Redis / Scheduler when present).
     */
    displayCards(): StackCard[] {
        const cards: StackCard[] = [{key: 'frontend', title: 'Frontend'}];
        cards.push(...this.backendStackCards());
        // Older backends may omit scheduler from active_components — still show a card.
        if (!cards.some((card) => card.key === 'scheduler')) {
            cards.push({key: 'scheduler', title: 'Scheduler'});
        }
        return cards;
    }

    /** Backend-driven dependency cards only (excludes local frontend). */
    backendStackCards(): StackCard[] {
        if (!this.platformHealth) {
            return [];
        }
        const fromBackend: string[] | undefined = this.platformHealth.active_components;
        const keys =
            Array.isArray(fromBackend) && fromBackend.length
                ? fromBackend
                : DEFAULT_ACTIVE_COMPONENTS.filter((key) => this.hasBackendComponent(key));
        return keys
            .filter((key) => this.hasBackendComponent(key))
            .map((key) => ({
                key,
                title: COMPONENT_TITLES[key] || key.replace(/_/g, ' '),
            }));
    }

    /** @deprecated Prefer displayCards(); kept for older specs that call stackCards(). */
    stackCards(): StackCard[] {
        return this.backendStackCards();
    }

    hasBackendComponent(name: string): boolean {
        const value = this.platformHealth?.[name];
        return value != null && typeof value === 'object';
    }

    /** Compatibility alias used by older specs. */
    hasComponent(name: string): boolean {
        if (name === 'frontend') {
            return true;
        }
        if (name === 'scheduler') {
            return true;
        }
        return this.hasBackendComponent(name);
    }

    statusClass(status: string | undefined | null): string {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'ok' || normalized === 'operational' || normalized === 'healthy') {
            return 'ok';
        }
        if (
            normalized === 'degraded' ||
            normalized === 'not_configured' ||
            normalized === 'not_monitored' ||
            normalized === 'unknown'
        ) {
            return 'degraded';
        }
        return 'down';
    }

    labelFor(status: string | undefined | null): string {
        if (!status) {
            return 'Unknown';
        }
        return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    component(name: string): HealthComponentState {
        if (name === 'frontend') {
            return FRONTEND_LOCAL_STATUS;
        }
        if (name === 'scheduler') {
            if (this.hasBackendComponent('scheduler')) {
                return this.platformHealth.scheduler || SCHEDULER_FALLBACK_STATUS;
            }
            return SCHEDULER_FALLBACK_STATUS;
        }
        return this.platformHealth?.[name] || {};
    }
}
